import type { RefObject } from 'react'
import { useEffect, useState } from 'react'

type useResizeObserverProperties = {
  /**
   * React ref object created with `useRef` to observe an element's size.
   */
  ref?: RefObject<Element> | null

  /**
   * DOM element obtained from methods like `querySelector()` to observe its size.
   */
  element?: Element | null | undefined

  /**
   * Optional callback function that triggers on resize event.
   * @param entry - The ResizeObserver entry containing size information.
   */
  callback?: (entry: ResizeObserverEntry) => void
}

/**
 * Custom React hook to observe the resizing of a DOM element.
 *
 * This hook uses the ResizeObserver API to track size changes of a given
 * element, either through a ref object or a directly passed DOM element.
 * It updates the component state with the latest `DOMRect` values of the observed element.
 *
 * @param {useResizeObserverProperties} hookProperties - Configuration options for the hook.
 *
 * @returns {DOMRectReadOnly} - The `DOMRect` object containing dimensions of the observed element.
 */
export const useResizeObserver = ({
  ref,
  element,
  callback
}: useResizeObserverProperties): DOMRectReadOnly => {
  const [sizes, setSizes] = useState<DOMRectReadOnly>(new DOMRect(0, 0, 0, 0))

  useEffect(() => {
    const domNode = ref?.current || element

    if (!domNode) {
      return
    }

    /**
     * Resize handler that processes the ResizeObserver entries and triggers updates.
     *
     * @param {ResizeObserverEntry[]} entries - Array of ResizeObserver entries.
     */
    const handleResize = (entries: ResizeObserverEntry[]) => {
      const [entry] = entries
      callback?.(entry)

      // Use requestAnimationFrame to avoid ResizeObserver loop limit warnings
      requestAnimationFrame(() => {
        const { width, height } = entry.contentRect

        setSizes((prevSizes) => {
          if (prevSizes.width !== width || prevSizes.height !== height) {
            return entry.contentRect
          }

          return prevSizes
        })
      })
    }

    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(domNode)

    return () => resizeObserver.disconnect()
  }, [ref, element, callback])

  /**
   * Error handler to suppress ResizeObserver errors for undelivered notifications.
   *
   * @param {Event} event - The error event to handle.
   */
  const resizeObserverErrorHandler = (event: Event) => {
    if (event instanceof ResizeObserverEntry) {
      event.preventDefault()
    }
  }

  useEffect(() => {
    window.addEventListener('error', resizeObserverErrorHandler)

    return () => window.removeEventListener('error', resizeObserverErrorHandler)
  }, [])

  return sizes
}
